import { FC, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ActionButton } from "@hdi-seguros/web-components.action-button";
import dayjs from "dayjs";
import { DeleteOutlined, DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { Flex, Table } from "antd";

import { displayErrorMessage, downloadBase64Files, encrypt, formattedPrice } from "../../../../../../shared/utils";
import PreLiquidationService from "../../../../../../shared/services/collection/preliquidation";

import { SavedAndSentPreLiquidationsContext } from "../../contexts/SavedAndSentPreLiquidations";

import { PreLiquidationTableItem } from "../../interfaces";

import './index.css';
import { FILE_TYPES, UserInformation } from "../../../../../../shared/interfaces";
import userHelper from "../../../../../../shared/utils/userHelper";
import { QUOTES_TYPE } from "../../../../PreLiquidation/shared/interfaces";

interface PreLiquidationsTableProps {
    isSavedPreliquidations?:   boolean;
};

export const PreLiquidationsTable: FC<PreLiquidationsTableProps> = ({
    isSavedPreliquidations = false,
}) => {
    const { 
        isLoadingPreLiquidations,
        datasourceSavedPreliquidationsTable,
        datasourceSentPreliquidationsTable,
        getSavedPreliquidations,
        setIsLoadingPreLiquidations,
    } = useContext(SavedAndSentPreLiquidationsContext);

    const navigate = useNavigate();
    
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const onDeletePreliquidation = useCallback(async (nroPreliquidation: string) => {
        try {
            setIsLoadingPreLiquidations(true);
            await PreLiquidationService.deletePreliquidation(nroPreliquidation);
            await getSavedPreliquidations();
        } catch (error) {} finally {
            setIsLoadingPreLiquidations(false);
        };
    }, [getSavedPreliquidations, setIsLoadingPreLiquidations]);

    const onDownloadPreliquidation = useCallback(async (preliquidationNumber: string) => {
        try {
            const responseSummaryQuotes = await PreLiquidationService.getSummaryQuotes({numeroPreliquidacion: preliquidationNumber, tipo: QUOTES_TYPE.MARKED });     
            const responsePaymentsList = await PreLiquidationService.getPaymentsList(preliquidationNumber);            
            const { intermediarioPrincipal }: UserInformation = userHelper.getUser()!;
        
            let hasRounding = false;
            responsePaymentsList?.data.Valores.forEach((payment) => {
                if(payment.formaDePago === "REDONDEO") {
                    hasRounding = true;
                }
            });

            const totalValue = responseSummaryQuotes?.data.cuotas?.reduce((acc, quote) => acc + Number(quote.importePesos), 0);
        
            const paymentsToRender = responsePaymentsList?.data.Valores.map(e => {
                return {
                    ...e,
                    importe: `$${formattedPrice(e.importe)}`,
                    fecha: dayjs(e.fecha).format("DD/MM/YYYY")
                }
            })

            const response = await PreLiquidationService.generatePreliquidationPdf({
                fechaVence: dayjs().format("DD/MM/YYYY"),
                fechaYHora: dayjs().format("DD/MM/YYYY"),
                nombreInter: intermediarioPrincipal?.nombreInter!,
                codigoInter: intermediarioPrincipal?.codigoInter!,
                numeroPreliquidacion: preliquidationNumber,
                preliquidacion: responseSummaryQuotes?.data.cuotas || [],
                productor: intermediarioPrincipal!,
                cma:  `${intermediarioPrincipal?.codMayorAux}-${intermediarioPrincipal?.nroMayorAux}`,
                formasDePagoSeleccionadas: paymentsToRender,
                total: `$ ${formattedPrice(`${totalValue}` || "0")}`,
                redondeoUsado: hasRounding,
            });
        
            if (response.data) {
                downloadBase64Files(response.data.base64, `Preliquidacion_${preliquidationNumber}.pdf`, FILE_TYPES.PDF);
            } else {
                displayErrorMessage("No se pudo generar el PDF de la preliquidación.");
            }
        } catch(error){
            displayErrorMessage("No se pudo generar el PDF de la preliquidación.");
        }
    }, []);

    const onResumePreliquidation = useCallback(( preliquidationNumber: string, preliquidationTotalAmount: string ) => {
        const encryptedData = encrypt({ preliquidationNumber, preliquidationTotalAmount });
        navigate(`/cobranzas/preliquidacion/${encryptedData}`)
    }, []);

    const HEADERS_PRELIQUIDATIONS_TABLE = useMemo(() => [
        {
            title: 'Número',
            dataIndex: 'preliquidationNumber',
            key: 'number',
        },
        {
            title: 'Fecha',
            dataIndex: 'preliquidationDate',
            key: 'date',
            render: (value: string) => <span>{value ? dayjs(value).format('DD/MM/YYYY') : '-'}</span>
        },
        {
            title: 'Importe Total',
            dataIndex: 'preliquidationTotalAmount',
            key: 'amount',
            render: (value: string) => <span>{value ? `$ ${formattedPrice(value)}` : '-'}</span>
        },
        {
            title: 'Estado',
            dataIndex: 'preliquidationState',
            key: 'state',
        },
        {   
            title: 'Acciones',
            dataIndex: 'action',
            key: 'actions',
            className: 'pre-liquidations-table-action-column',
            width: '10%',
            render: (_: any, { preliquidationNumber, shouldResumePreliquidation, preliquidationTotalAmount }: PreLiquidationTableItem) => (
                <>  
                    {
                        isSavedPreliquidations 
                            ? (
                                <Flex gap={10} justify="center">
                                    <ActionButton 
                                        label="" 
                                        icon={<EditOutlined />} 
                                        onClick={() => onResumePreliquidation(preliquidationNumber, preliquidationTotalAmount)}
                                        type="secondary"
                                        disabled={!shouldResumePreliquidation}
                                    />

                                    <ActionButton 
                                        label="" 
                                        icon={<DeleteOutlined />} 
                                        color="danger"
                                        type="secondary"
                                        onClick={() => onDeletePreliquidation(preliquidationNumber)}
                                    />
                                </Flex>
                            )
                            : (
                                <Flex justify="center">
                                    <ActionButton 
                                        label="" 
                                        icon={<DownloadOutlined />} 
                                        onClick={() => onDownloadPreliquidation(preliquidationNumber)}
                                        type="secondary"
                                    />
                                </Flex>
                            )
                    }
                </>
            )
        },
    ], [
        onResumePreliquidation, 
        onDeletePreliquidation, 
        onDownloadPreliquidation,
        isSavedPreliquidations,
    ]);

    const onPageSizeChange = useCallback((current: number, newSize: number) => {
        if(current === pageSize) return;
        setPageSize(newSize);
    }, [pageSize, setPageSize]);

    return (
        <Table
            loading={isLoadingPreLiquidations}
            bordered
            className='w-100'
            columns={HEADERS_PRELIQUIDATIONS_TABLE}
            dataSource={
                isSavedPreliquidations
                    ? datasourceSavedPreliquidationsTable
                    : datasourceSentPreliquidationsTable
            }
            pagination={{
                current: currentPage,
                pageSize: pageSize,
                showTotal: (total: number) => `${total} ${ total > 1 ? 'preliquidaciones' : 'preliquidación'}`,
                showSizeChanger: true,
                onShowSizeChange: onPageSizeChange,
                total: (
                    isSavedPreliquidations
                        ? datasourceSavedPreliquidationsTable.length
                        : datasourceSentPreliquidationsTable.length
                ),
                onChange: (page) => {
                    setCurrentPage(page);
                },
            }}
            rowKey={(item) => item.preliquidationNumber}
        />
    )
}
